import React, { useState, useRef, useEffect } from "react";
import { FiCalendar, FiChevronDown, FiChevronUp } from "react-icons/fi";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { endOfDay, endOfMonth, endOfYear, startOfDay, startOfMonth, startOfYear, format } from "date-fns";

import {
  FilterWrapper,
  FilterButton,
  DropdownContainer,
  ContentContainer,
  OptionsList,
  RadioOption,
  CustomDateContainer,
  DateInputWrapper,
  Footer
} from "./styles";

import { PrimaryButton } from "../PrimaryButton";
import { SecondaryButton } from "../SecondaryButton";
import { localeMocks } from "../../utils/dateMocks";
import { getActiveLanguage } from "../../utils/getActiveLanguage";
import { IPeriod } from "../../modules/Orders/dtos";

export type DateFilterOption = "today" | "this_month" | "this_year" | "all_time" | "custom";

interface DateDropdownFilterProps {
  onApply: (period: IPeriod | undefined, option: DateFilterOption) => void;
  defaultOption?: DateFilterOption;
  defaultPeriod?: IPeriod;
}

export function DateDropdownFilter({ 
  onApply, 
  defaultOption = "this_year",
  defaultPeriod
}: DateDropdownFilterProps) {
  const activeLanguage = getActiveLanguage();
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DateFilterOption>(defaultOption);
  
  // Custom dates state
  const [customFrom, setCustomFrom] = useState<Date>(defaultPeriod?.from || startOfMonth(new Date()));
  const [customTo, setCustomTo] = useState<Date>(defaultPeriod?.to || endOfMonth(new Date()));

  // Active applied state to display on the button
  const [activeAppliedOption, setActiveAppliedOption] = useState<DateFilterOption>(defaultOption);
  const [activeAppliedCustom, setActiveAppliedCustom] = useState<IPeriod | null>(
    defaultPeriod ? defaultPeriod : null
  );

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Reset unsaved changes when closing
        setSelectedOption(activeAppliedOption);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeAppliedOption]);

  const handleApply = () => {
    setActiveAppliedOption(selectedOption);
    
    let period: IPeriod | undefined;
    const now = new Date();

    switch (selectedOption) {
      case "today":
        period = { from: startOfDay(now), to: endOfDay(now) };
        break;
      case "this_month":
        period = { from: startOfMonth(now), to: endOfMonth(now) };
        break;
      case "this_year":
        period = { from: startOfYear(now), to: endOfYear(now) };
        break;
      case "all_time":
        period = undefined; // Return undefined to fetch everything
        break;
      case "custom":
        period = { from: startOfDay(customFrom), to: endOfDay(customTo) };
        setActiveAppliedCustom(period);
        break;
    }

    onApply(period, selectedOption);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedOption(defaultOption);
    setActiveAppliedOption(defaultOption);

    let period: IPeriod | undefined;
    const now = new Date();

    switch (defaultOption) {
      case "today":
        period = { from: startOfDay(now), to: endOfDay(now) };
        break;
      case "this_month":
        period = { from: startOfMonth(now), to: endOfMonth(now) };
        break;
      case "this_year":
        period = { from: startOfYear(now), to: endOfYear(now) };
        break;
      case "all_time":
        period = undefined;
        break;
      case "custom":
        period = { from: startOfDay(customFrom), to: endOfDay(customTo) };
        setActiveAppliedCustom(period);
        break;
    }

    onApply(period, defaultOption);
    setIsOpen(false);
  };

  // Compute what text to show on the button
  const getButtonText = () => {
    switch (activeAppliedOption) {
      case "today": return "Hoje";
      case "this_month": return "Este mês";
      case "this_year": return "Este ano";
      case "all_time": return "Desde o início";
      case "custom": 
        if (activeAppliedCustom) {
          return `${format(activeAppliedCustom.from, "dd/MM/yyyy")} até ${format(activeAppliedCustom.to, "dd/MM/yyyy")}`;
        }
        return "Personalizado";
      default: return "Filtros";
    }
  };

  return (
    <FilterWrapper ref={wrapperRef} className={isOpen ? "open" : ""}>
      <FilterButton onClick={() => setIsOpen(!isOpen)} active={isOpen} type="button">
        <FiCalendar size={16} />
        <span>{getButtonText()}</span>
        {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
      </FilterButton>

      {isOpen && (
        <DropdownContainer>
          <ContentContainer>
            <OptionsList>
              <RadioOption>
                <input
                  type="radio"
                  name="date-filter"
                  checked={selectedOption === "today"}
                  onChange={() => setSelectedOption("today")}
                />
                <span className="radio-custom"></span>
                <span className="label">Hoje</span>
              </RadioOption>
              
              <RadioOption>
                <input
                  type="radio"
                  name="date-filter"
                  checked={selectedOption === "this_month"}
                  onChange={() => setSelectedOption("this_month")}
                />
                <span className="radio-custom"></span>
                <span className="label">Este mês</span>
              </RadioOption>
              
              <RadioOption>
                <input
                  type="radio"
                  name="date-filter"
                  checked={selectedOption === "this_year"}
                  onChange={() => setSelectedOption("this_year")}
                />
                <span className="radio-custom"></span>
                <span className="label">Este ano</span>
              </RadioOption>
              
              <RadioOption>
                <input
                  type="radio"
                  name="date-filter"
                  checked={selectedOption === "all_time"}
                  onChange={() => setSelectedOption("all_time")}
                />
                <span className="radio-custom"></span>
                <span className="label">Desde o início</span>
              </RadioOption>
              
              <RadioOption>
                <input
                  type="radio"
                  name="date-filter"
                  checked={selectedOption === "custom"}
                  onChange={() => setSelectedOption("custom")}
                />
                <span className="radio-custom"></span>
                <span className="label">Personalizado</span>
              </RadioOption>

              {selectedOption === "custom" && (
                <CustomDateContainer>
                  <DateInputWrapper>
                    <DatePicker
                      selected={customFrom}
                      onChange={(date: Date) => setCustomFrom(date || new Date())}
                      dateFormat="dd/MM/yyyy"
                      locale={localeMocks[activeLanguage ?? "en"]}
                    />
                    <FiCalendar size={14} />
                  </DateInputWrapper>
                  <span className="separator">até</span>
                  <DateInputWrapper>
                    <DatePicker
                      selected={customTo}
                      onChange={(date: Date) => setCustomTo(date || new Date())}
                      minDate={customFrom}
                      dateFormat="dd/MM/yyyy"
                      locale={localeMocks[activeLanguage ?? "en"]}
                    />
                    <FiCalendar size={14} />
                  </DateInputWrapper>
                </CustomDateContainer>
              )}
            </OptionsList>
          </ContentContainer>

          <Footer>
            <SecondaryButton text="Limpar" onClick={handleClear} style={{ height: "36px", padding: "8px 16px" }} />
            <PrimaryButton text="Aplicar" onClick={handleApply} style={{ height: "36px", padding: "8px 24px" }} />
          </Footer>
        </DropdownContainer>
      )}
    </FilterWrapper>
  );
}
